import { DocumentPage } from "../DocumentChat/Components";
import DocumentsTable from "./DocumentsTable";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Document, DocumentFormat, DocumentStatus, FetchDocumentsByTenderIdDocument, FetchDocumentsByTenderIdQuery, FetchDocumentsByTenderIdQueryVariables } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { DocxRenderComponent, TextRenderComponent } from "@/components/shared";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";

const hasProcessingDocuments = (documents: Document[]): boolean => {
  return documents.some(
    (doc: Document) =>
      doc.status === DocumentStatus.AddingToKnowledgeBase ||
      doc.status === DocumentStatus.RemovingFromKnowledgeBase
  );
};

function DocumentsComponent() {
  const { t } = useTranslation(["translation", "common"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [mutationPollingActive, setMutationPollingActive] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading, refetch } = useQuery<FetchDocumentsByTenderIdQuery, FetchDocumentsByTenderIdQueryVariables>(FetchDocumentsByTenderIdDocument, {
    variables: { tenderId: Number(tenderID) },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: false, // Prevents Apollo from updating the loading state during polling refetches
  });

  const documents = data?.fetchDocumentsByTenderId || [];

  // Check if any documents are being processed
  const isProcessing = hasProcessingDocuments(documents);

  // Polling logic
  useEffect(() => {
    // Clear any existing polling timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Start polling if there are processing documents
    if (isProcessing && !mutationPollingActive) {
      const pollInterval = 10000; // 10 seconds
      const maxTimeout = 10 * 60 * 1000; // 10 minutes maximum
      const startTime = Date.now();

      const pollForUpdates = async () => {
        if (mutationPollingActive) {
          return;
        }

        // Check timeout
        if (Date.now() - startTime > maxTimeout) {
          console.error("Timeout reached while polling for document status updates");
          return;
        }

        try {
          // Refetch documents
          const result = await refetch({ tenderId: Number(tenderID) });
          const updatedDocuments = result.data?.fetchDocumentsByTenderId || [];

          const stillProcessing = hasProcessingDocuments(updatedDocuments);

          if (stillProcessing) {
            // Continue polling
            pollingTimeoutRef.current = setTimeout(pollForUpdates, pollInterval);
          }
        } catch (error) {
          console.error("Error while polling for document updates:", error);
        }
      };

      // Start polling
      pollingTimeoutRef.current = setTimeout(pollForUpdates, pollInterval);
    }

    // Cleanup function
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [isProcessing, refetch, tenderID, mutationPollingActive]);

  useEffect(() => {
    if (!loading && documents.length > 0) {
      setSelectedDocument(documents[0]);
    }
  }, [loading, documents]);

  return (
    <div className="h-[calc(100vh-105px)] pr-4 pl-2">
      <ResizablePanelGroup orientation="horizontal" className="h-full">
        <ResizablePanel defaultSize={50} minSize={20}>
          <div className="pr-4 pt-2">
            <DocumentsTable
              documents={documents}
              setSelectedDocument={setSelectedDocument}
              selectedDocument={selectedDocument}
              loading={loading}
              setMutationPollingActive={setMutationPollingActive}
            />
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle className="mr-4"/>
        <ResizablePanel defaultSize={50} minSize={30}>
          {selectedDocument?.format === DocumentFormat.Pdf ? (
            <DocumentPage
              key={selectedDocument.id}
              pdfDocument={selectedDocument}
            />
          ) : selectedDocument?.format === DocumentFormat.Txt ? (
            <TextRenderComponent selectedDocument={selectedDocument}/>
          ) : selectedDocument?.format === DocumentFormat.Docx ? (
            <DocxRenderComponent selectedDocument={selectedDocument}/>
          ) : selectedDocument && (
            <div className="p-4 flex flex-col gap-4 h-full">
              <p>{selectedDocument.fileName}</p>
              <p className="text-sm text-gray-400">
                {t("edittender.documents.unsupportedfiletype", { format: selectedDocument.format })}
              </p>
              <Button variant="outline" onClick={() => {
                window.open(selectedDocument.url, "_blank");
              }}>
                {t("common:buttons.download")}
              </Button>
            </div>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

export default DocumentsComponent;
